import { useFormContext, useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { useCalendarData } from '@calendly/ui/hooks/use-calendar-data';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useState, forwardRef } from 'react';
import Calendar from 'components/availability/form/calendar_view/calendar';
import Navigation from 'components/availability/form/calendar_view/navigation';
import { getBookingRangeInitialValues } from 'components/event_type_edit';
import { Layout } from 'components/layout';
import { useEventType } from 'features/event_type_provider';
import { getEventTypePeriodEnd, getEventTypePeriodStart } from 'selectors/availability/schedules';
import { isEditorPreviewCalendarVariant } from './calendar_variant';
import { TimezoneDropdown } from '../../timezone_dropdown';
const controlRowCss = "c10zdoag";
const ControlRow = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(controlRowCss, props.className)} />;
});
const CalendarControls = props => <Layout gap="16" justify="space-between" grow align="center" paddingTop="24" {...props} />;

/* Figma show-calendar: spacing1_5x inset comes from the preview panel */
const editorPreviewShellCss = "e1xp817f";
const editorPreviewHeaderCss = "e13pnivv";
const editorPreviewHeaderLeadingCss = "eidr7fk";
const editorPreviewTimezoneCss = "em3qzmk";
const editorPreviewFooterCss = "eba75dx";
const calendarContainerCss = "c1ozf8tq";
const editorPreviewCalendarContainerCss = "eoltiu5";
const CalendarContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    isEditorPreview = false,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(isEditorPreview ? editorPreviewCalendarContainerCss : calendarContainerCss, props.className)} />;
});
export const AvailabilityCalendar = ({
  formPath,
  onClose,
  variant
}) => {
  const {
    setFieldValue
  } = useFormContext();
  const [{
    value: {
      availableHours,
      timezone,
      dateOverrides
    }
  }] = useFormField({
    name: formPath
  });
  const setDateOverrides = overrides => setFieldValue(`${formPath}.dateOverrides`, overrides);
  const setAvailableHours = hours => setFieldValue(`${formPath}.availableHours`, hours);
  const [{
    value: holidays
  }] = useFormField({
    name: `${formPath}.holidays`
  });
  const [{
    value: userHolidaySet
  }] = useFormField({
    name: `${formPath}.userHolidaySet`
  });
  const setUsingCustomHours = () => {
    setFieldValue(`${formPath}.usingCustomHours`, true);
    setFieldValue(`${formPath}.usingSecondarySchedule`, false);
  };
  const onDatesUpdated = overrides => {
    setUsingCustomHours();
    setDateOverrides(overrides);
  };
  const setWdayIntervals = (day, intervals) => {
    const updatedHours = {
      ...availableHours,
      [day]: intervals
    };
    setUsingCustomHours();
    setAvailableHours(updatedHours);
  };
  const now = moment().tz(timezone).format('YYYY-MM');
  const yearFromNow = moment().tz(timezone).add(1, 'year').format('YYYY-MM');
  const [currentMonthId, setCurrentMonthId] = useState(now);
  const {
    title,
    weekdays,
    weeks,
    nextMonthId,
    prevMonthId,
    isCurrentMonth
  } = useCalendarData(currentMonthId, timezone);
  const eventType = useEventType();
  const {
    periodType,
    startDate,
    movingType,
    maxBookingTime,
    endDate
  } = getBookingRangeInitialValues(eventType);
  const activeFrom = getEventTypePeriodStart(periodType, startDate, timezone);
  const activeTo = getEventTypePeriodEnd(periodType, movingType, maxBookingTime, endDate, timezone);
  const isEditorPreview = isEditorPreviewCalendarVariant(variant);
  const calendar = <Calendar allowWdayUpdates days={availableHours} dates={dateOverrides} holidays={holidays} userHolidaySet={userHolidaySet} timezone={timezone} weekdays={weekdays} weeks={weeks} setDates={onDatesUpdated} setHolidays={() => {}} setUserHolidaySet={() => {}} setWdayIntervals={setWdayIntervals} activeFrom={activeFrom} activeTo={activeTo} activeWeekends={periodType !== 'rollingDays' || movingType !== 'business'} isFullHeight={!isEditorPreview} useUpdatedStyles readOnlyHolidays horizontalSpacers={!isEditorPreview} isEditorPreview={isEditorPreview} />;
  if (isEditorPreview) {
    return <div className={editorPreviewShellCss} data-testid="editor-preview-availability-calendar">
        <div className={editorPreviewHeaderCss}>
          <div className={editorPreviewHeaderLeadingCss}>
            <Navigation alternateStyling isCurrentMonth={isCurrentMonth} isLastMonth={nextMonthId === yearFromNow} nextMonthId={nextMonthId} prevMonthId={prevMonthId} setMonth={setCurrentMonthId} title={title} titlePosition="leading" useUpdatedStyles />
          </div>
          <div className={editorPreviewTimezoneCss}>
            <TimezoneDropdown expandLeft formPath={formPath} />
          </div>
          {onClose && <IconButton aria-label={I18n.t('publisher.action.close')} icon={CloseIcon} size="small" title={I18n.t('publisher.action.close')} onClick={onClose} />}
        </div>
        <CalendarContainer isEditorPreview>
          {calendar}
        </CalendarContainer>
        <div className={editorPreviewFooterCss}>
          <BodyText color="muted" size="small">
            {I18n.t('publisher.event_type_editor_crud.availability.calendar_preview_hint')}
          </BodyText>
        </div>
      </div>;
  }
  return <>
      <ControlRow>
        <CalendarControls>
          <Layout align="center">
            <Navigation isCurrentMonth={isCurrentMonth} isLastMonth={nextMonthId === yearFromNow} setMonth={setCurrentMonthId} prevMonthId={prevMonthId} nextMonthId={nextMonthId} title={title} useUpdatedStyles alternateStyling />
          </Layout>
          <TimezoneDropdown formPath={formPath} expandLeft />
        </CalendarControls>
      </ControlRow>
      <CalendarContainer>
        {calendar}
      </CalendarContainer>
    </>;
};

require("./availability_calendar.wyw-in-js.css!=!../../../../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./availability_calendar.js");